import React, { useEffect, useState } from "react";
import { Switch, notification } from "antd";
import { API_URL } from "env";
import { deleteFmcToken } from "services/fmc_token_upload";
import { initializeApp } from "firebase/app";
import { getAnalytics } from "firebase/analytics";
import { firebaseConfig, vapidKey } from 'services/firebase_config';
import { getMessaging, onMessage, getToken, isSupported, Messaging } from "firebase/messaging";
import { uploadFmcToken } from 'services/fmc_token_upload';

// Khởi tạo app và analytics một lần ngoài component
const app = initializeApp(firebaseConfig);

type NotificationType = 'success' | 'info' | 'warning' | 'error';

export const FirebaseNotificationToggle: React.FC = () => {
    // Khởi tạo state đọc từ localStorage (mặc định là false nếu chưa lưu)
    const [isSubscribed, setIsSubscribed] = useState<boolean>(() => {
        const savedStatus = localStorage.getItem('firebase_notifications_enabled');
        return savedStatus === 'true';
    });
    const [loading, setLoading] = useState<boolean>(false);
    const [messaging, setMessaging] = useState<Messaging | null>(null);
    const token = localStorage.getItem('token');
    const [api, contextHolder] = notification.useNotification();

    const openNotificationWithIcon = (type: NotificationType, message: string, description: string) => {
        api[type]({
            message,
            description,
            placement: 'topRight',
        });
    };

    // 1. Kiểm tra hỗ trợ trình duyệt và khởi tạo messaging
    useEffect(() => {
        isSupported().then((supported) => {
            if (supported) {
                const msg = getMessaging(app);
                setMessaging(msg);
                console.log("FCM được hỗ trợ trên trình duyệt này.");
            } else {
                console.log("Trình duyệt không hỗ trợ FCM.");
            }
        }).catch(err => console.error("Lỗi kiểm tra support:", err));
    }, []);

    // 2. Hàm đăng ký nhận thông báo Firebase (chỉ chuyển state & lưu localStorage khi upload thành công)
    const requestNotificationPermission = async () => {
        if (!messaging) return;
        setLoading(true);
        try {
            const permission = await Notification.requestPermission();

            if (permission === "granted") {
                const registration = await navigator.serviceWorker.register('/firebase-messaging-sw.js');
                await navigator.serviceWorker.ready;

                const fcmToken = await getToken(messaging, {
                    vapidKey: vapidKey,
                    serviceWorkerRegistration: registration
                });

                console.log('Your FCM Device Token:', fcmToken);
                if (fcmToken && token) {
                    // Đợi uploadFmcToken chạy thành công
                    await uploadFmcToken(fcmToken);

                    setIsSubscribed(true);
                    localStorage.setItem('firebase_notifications_enabled', 'true');
                    openNotificationWithIcon('success', 'Thành công', 'Đã bật nhận thông báo thành công');
                } else {
                    openNotificationWithIcon('error', 'Lỗi', 'Không lấy được FCM Token hoặc thiếu token xác thực');
                }
            } else {
                setIsSubscribed(false);
                localStorage.setItem('firebase_notifications_enabled', 'false');
                openNotificationWithIcon('info', 'Thông báo', 'Bạn đã từ chối quyền nhận thông báo');
            }
        } catch (err) {
            console.error('An error occurred while registering:', err);
            openNotificationWithIcon('error', 'Lỗi', 'Lỗi khi đăng ký nhận thông báo');
            setIsSubscribed(false);
            localStorage.setItem('firebase_notifications_enabled', 'false');
        } finally {
            setLoading(false);
        }
    };

    // 3. Hàm tắt nhận thông báo từ Firebase (chỉ chuyển state & cập nhật localStorage khi delete thành công)
    const handleDisableNotifications = async () => {
        setLoading(true);
        try {
            const user = localStorage.getItem('user');
            if (!user) {
                setLoading(false);
                return;
            }

            // Gọi hàm xóa FCM token và đợi thực thi thành công
            await deleteFmcToken();

            setIsSubscribed(false);
            localStorage.setItem('firebase_notifications_enabled', 'false');
            openNotificationWithIcon('success', 'Thành công', 'Đã tắt nhận thông báo Firebase');
        } catch (error) {
            console.error('Lỗi khi tắt thông báo:', error);
            openNotificationWithIcon('error', 'Lỗi', 'Không thể tắt thông báo');
        } finally {
            setLoading(false);
        }
    };

    // 4. Lắng nghe foreground messages (khi ứng dụng đang mở)
    useEffect(() => {
        if (messaging) {
            const unsubscribe = onMessage(messaging, (payload) => {
                console.log('Message received in foreground: ', payload);
                openNotificationWithIcon('info', payload.notification?.title || 'Thông báo mới', payload.notification?.body || '');
            });
            return () => unsubscribe();
        }
    }, [messaging]);

    // Xử lý sự kiện bật/tắt Switch
    const handleToggle = (checked: boolean) => {
        if (checked) {
            requestNotificationPermission();
        } else {
            handleDisableNotifications();
        }
    };

    return (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }} onClick={(e) => e.stopPropagation()}>
            {contextHolder}
            <Switch
                checked={isSubscribed}
                loading={loading}
                onChange={handleToggle}
                size="small"
            />
            <span style={{ fontSize: '12px', fontWeight: 600, color: 'var(--ink, #0b1c2e)' }}>
                {isSubscribed ? 'Nhận thông báo' : 'Tắt thông báo'}
            </span>
        </div>
    );
};